import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";

// Linguagem amigável para Ação e Recurso (em vez de termos técnicos como "tenant.created")
const ACTION_LABELS = {
  "tenant.created": "Empresa criada",
  "tenant.updated": "Empresa atualizada",
  "tenant.template_pack_applied": "Pacote da vertical aplicado",
  "auth.impersonate": "Entrou como administrador",
  "auth.login": "Acessou o sistema",
  "auth.login.success": "Acessou o sistema",
  "auth.login.failed": "Falha de login",
  "auth.logout": "Saiu do sistema",
  "team.create": "Funcionário cadastrado",
  "team.update": "Funcionário atualizado",
  "team.delete": "Funcionário removido",
  "client.created": "Cliente cadastrado",
  "client.updated": "Cliente atualizado",
  "client.deleted": "Cliente removido",
  "appointment.created": "Atendimento agendado",
  "appointment.updated": "Atendimento atualizado",
  "appointment.completed": "Atendimento concluído",
  "appointment.cancelled": "Atendimento cancelado",
  "sale.completed": "Venda realizada",
  "communique.create": "Comunicado enviado",
  "communique.resend": "Comunicado reenviado",
  "responsible.create": "Responsável cadastrado",
  "campaign_preset.installed": "Campanha pronta instalada",
  "loyalty.rule_created": "Regra de fidelidade criada",
  "loyalty.rule_updated": "Regra de fidelidade atualizada",
  "message_template.created": "Modelo de mensagem criado",
  "message_template.updated": "Modelo de mensagem atualizado",
  "branding.updated": "Identidade visual atualizada",
  "feature_flags.updated": "Recursos atualizados",
  "custom_fields.updated": "Campos personalizados atualizados",
};

const RESOURCE_LABELS = {
  tenant: "Empresa",
  user: "Funcionário",
  client: "Cliente",
  appointment: "Atendimento",
  sale: "Venda",
  communique: "Comunicado",
  responsible: "Responsável",
  template: "Modelo da vertical",
  message_template: "Modelo de mensagem",
  campaign: "Campanha",
  loyalty_rule: "Regra de fidelidade",
  branding: "Identidade visual",
  feature_flags: "Recursos",
  custom_fields: "Campos personalizados",
};

export default function TenantAudit({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tenant/audit", { params: { limit: 200 } });
        setItems(data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const table = (
    <Card className="rounded-tenant border-border overflow-hidden">
      {loading ? (
        <div className="p-10 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground">Nenhuma atividade registrada ainda.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Quando</th>
              <th className="text-left px-5 py-3 font-medium">O que aconteceu</th>
              <th className="text-left px-5 py-3 font-medium">Quem fez</th>
              <th className="text-left px-5 py-3 font-medium">Item afetado</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-border">
                <td className="px-5 py-2.5 text-xs whitespace-nowrap">{new Date(a.created_at).toLocaleString("pt-BR")}</td>
                <td className="px-5 py-2.5 font-medium">{ACTION_LABELS[a.action] || a.action}</td>
                <td className="px-5 py-2.5 text-muted-foreground">{a.actor_email}</td>
                <td className="px-5 py-2.5 text-xs">{RESOURCE_LABELS[a.resource] || a.resource}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );

  if (embedded) return table;

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Histórico de alterações</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Histórico desta empresa</h1>
        <p className="text-sm text-muted-foreground mt-1">Tudo que aconteceu — escopado por tenant.</p>
      </div>
      {table}
    </div>
  );
}
